import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { list_pages, get_page_images, create_image, update_image, delete_image, get_image, get_image_file } from '../../api/functions';
import { useApi } from '../../api/api';
import { LoadingSpinner, AdminEndpoint } from '../../components';
import './TabContent.css';

const ImagesTab = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const listPagesApi = useApi(list_pages);
  const getPageImagesApi = useApi(get_page_images);
  const createImageApi = useApi(create_image);
  const updateImageApi = useApi(update_image);
  const deleteImageApi = useApi(delete_image);
  const getImageApi = useApi(get_image);
  const getImageFileApi = useApi(get_image_file);
  const gridRef = useRef(null);

  const [editingImage, setEditingImage] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copyFiles, setCopyFiles] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [allImages, setAllImages] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [loadedPagesCount, setLoadedPagesCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);

  const PAGE_BATCH_SIZE = 50;
  const ROWS_PER_PAGE = 4;

  const fetchImages = useCallback(async () => {
    const pagesData = await listPagesApi.execute({
      skip: 0,
      limit: PAGE_BATCH_SIZE,
      show_private: true
    });

    if (pagesData) {
      setAllPages(pagesData);
      setLoadedPagesCount(pagesData.length);
      setHasMorePages(pagesData.length === PAGE_BATCH_SIZE);

      const imagesPromises = pagesData.map(page =>
        getPageImagesApi.execute({ page_id: page.id }).catch(() => [])
      );

      const imagesArrays = await Promise.all(imagesPromises);
      const allImgs = imagesArrays.flat();

      const filteredImgs = searchQuery
        ? allImgs.filter(img => {
          const title = getLocalizedField(img, 'title').toLowerCase();
          return title.includes(searchQuery.toLowerCase());
        })
        : allImgs;

      setAllImages(filteredImgs);
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadMorePages = useCallback(async () => {
    if (!hasMorePages || listPagesApi.loading) return;

    const pagesData = await listPagesApi.execute({
      skip: loadedPagesCount,
      limit: PAGE_BATCH_SIZE,
      show_private: true
    });

    if (pagesData && pagesData.length > 0) {
      const newPages = [...allPages, ...pagesData];
      setAllPages(newPages);
      setLoadedPagesCount(loadedPagesCount + pagesData.length);
      setHasMorePages(pagesData.length === PAGE_BATCH_SIZE);

      const imagesPromises = pagesData.map(page =>
        getPageImagesApi.execute({ page_id: page.id }).catch(() => [])
      );

      const imagesArrays = await Promise.all(imagesPromises);
      const newImgs = imagesArrays.flat();

      setAllImages([...allImages, ...newImgs]);
    } else {
      setHasMorePages(false);
    }
  }, [allPages, allImages, loadedPagesCount, hasMorePages, listPagesApi.loading]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchImages();
    }
  }, [fetchImages]);

  useEffect(() => {
    const calculateColumns = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const minCardWidth = 280;
        const gap = 24;
        const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
        const newItemsPerPage = ROWS_PER_PAGE * columns;
        setItemsPerPage(newItemsPerPage);
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new');

    if (isCreatingPath) {
      setIsCreating(true);
      setEditingImage(null);
    } else if (id && currentPath.includes('/edit')) {
      setCopyFiles({});
      const fetchImageForEdit = async () => {
        const imageItem = await getImageApi.execute({ image_id: parseInt(id) });
        if (imageItem) {
          setEditingImage(imageItem);
          setIsCreating(false);
        }
      };
      fetchImageForEdit();
    } else {
      setIsCreating(false);
      setEditingImage(null);
      setCopyFiles({});
    }
  }, [id, params]);

  useEffect(() => {
    const neededImages = currentPage * itemsPerPage;
    if (neededImages > allImages.length && hasMorePages && !listPagesApi.loading) {
      loadMorePages();
    }
  }, [currentPage, itemsPerPage, allImages.length, hasMorePages, listPagesApi.loading, loadMorePages]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedImages = allImages.slice(startIndex, endIndex);
  const totalPages = Math.ceil(allImages.length / itemsPerPage);

  const handleCreate = () => {
    navigate('/admin/images/new');
  };

  const handleEdit = (imageItem) => {
    navigate(`/admin/images/${imageItem.id}/edit`);
  };

  const handleCancel = () => {
    navigate('/admin/images');
  };

  const storageKey = isCreating ? 'admin_images_create' : (id ? `admin_images_edit_${id}` : null);

  const handleCopy = async (imageItem) => {
    const copyData = {
      title_en: imageItem.title_en || '',
      title_cz: imageItem.title_cz || '',
      title_de: imageItem.title_de || '',
      private: imageItem.private || false,
    };

    let copiedFile = null;
    if (imageItem.image_filename) {
      const blob = await getImageFileApi.execute({ image_id: imageItem.id });
      if (blob) {
        copiedFile = new File([blob], imageItem.image_filename, { type: blob.type });
      }
    }
    setCopyFiles(copiedFile ? { image: copiedFile } : {});

    localStorage.setItem('admin_images_create', JSON.stringify(copyData));
    navigate('/admin/images/new');
  };

  const handleSubmit = async (formData) => {
    if (isCreating) {
      const result = await createImageApi.execute({ ...formData });
      if (createImageApi.error) {
        toast.error(createImageApi.error || 'An error occurred while creating the image', { position: "bottom-right" });
        return;
      }
      if (result) {
        toast.success('Image created successfully!', { position: "bottom-right" });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchImages();
        navigate('/admin/images');
      }
    } else if (editingImage) {
      const result = await updateImageApi.execute({ ...formData, image_id: editingImage.id });
      if (updateImageApi.error) {
        toast.error(updateImageApi.error || 'An error occurred while updating the image', { position: "bottom-right" });
        return;
      }
      if (result) {
        toast.success('Image updated successfully!', { position: "bottom-right" });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchImages();
        navigate('/admin/images');
      }
    }
  };

  const handleDelete = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      const result = await deleteImageApi.execute({ image_id: imageId });
      if (deleteImageApi.error) {
        toast.error(deleteImageApi.error || 'An error occurred while deleting the image', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Image deleted successfully!', {
          position: "bottom-right"
        });
        await fetchImages();
        navigate('/admin/images');
      }
    }
  };

  const getLocalizedField = (item, fieldPrefix) => {
    const language = i18n.language;
    if (item[`${fieldPrefix}_${language}`]) {
      return item[`${fieldPrefix}_${language}`];
    }
    if (item[`${fieldPrefix}_cz`]) {
      return item[`${fieldPrefix}_cz`];
    }
    if (item[`${fieldPrefix}_en`]) {
      return item[`${fieldPrefix}_en`];
    }
    if (item[`${fieldPrefix}_de`]) {
      return item[`${fieldPrefix}_de`];
    }
    return '';
  };

  const getPageName = (pageId) => {
    const page = allPages.find(p => p.id === pageId);
    if (!page) return `Page #${pageId}`;
    return getLocalizedField(page, 'title') || `Page #${pageId}`;
  };

  const pageOptions = allPages.map(page => ({
    value: page.id,
    label: `#${page.id} - ${getLocalizedField(page, 'title') || 'Untitled Page'}`
  }));

  const imageFields = [
    {
      type: 'language_tabs',
      name: 'content_tabs',
      languages: ['en', 'cz', 'de'],
      fields: [
        { name: 'title', label: t('admin.fields.title'), type: 'text', required: false, placeholder: t('admin.placeholders.imageTitle') }
      ]
    },
    {
      name: 'page_id',
      label: t('admin.fields.page'),
      type: 'select',
      required: true,
      options: pageOptions,
      className: 'page-select-field'
    },
    { name: 'private', label: t('admin.fields.membersOnly'), type: 'boolean', defaultValue: false },
    { name: 'image', label: t('admin.fields.imageFile'), type: 'file', accept: 'image/*', currentFile: editingImage?.image_filename || (isCreating ? copyFiles.image?.name : undefined) }
  ];

  if (isCreating || editingImage) {
    return (
      <div className="tab-container">
        <div className="tab-header">
          <h2 className="tab-title">{isCreating ? t('admin.images.createTitle') : t('admin.images.editTitle')}</h2>
          <p className="tab-description">
            {isCreating ? t('admin.images.createDesc') : `${t('admin.images.editDesc')}: ${getLocalizedField(editingImage, 'title')}`}
          </p>
        </div>

        <div className="edit-actions-top">
          <button className="cancel-btn-top" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          {editingImage && (
            <button className="copy-btn-top" onClick={() => handleCopy(editingImage)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t('common.copy', 'Copy')}
            </button>
          )}
          {editingImage && (
            <button
              className="delete-btn"
              onClick={() => handleDelete(editingImage.id)}
              disabled={deleteImageApi.loading}
            >
              {deleteImageApi.loading ? t('common.deleting') : t('admin.images.delete')}
            </button>
          )}
        </div>

        <AdminEndpoint
          fields={imageFields}
          onSubmit={handleSubmit}
          onCancel={null}
          initialData={isCreating ? { ...copyFiles } : (editingImage || {})}
          submitLabel={isCreating ? t('admin.images.createBtn') : t('admin.images.updateBtn')}
          isLoading={createImageApi.loading || updateImageApi.loading}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">{t('admin.images.title')}</h2>
        </div>
        <button className="create-btn" onClick={handleCreate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.images.createBtn')}
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder={t('admin.images.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {(listPagesApi.loading || getPageImagesApi.loading) && allImages.length === 0 ? (
        <LoadingSpinner message={t('common.loading', 'Loading images...')} />
      ) : allImages.length === 0 ? (
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>{t('admin.images.noImages')}</p>
          <button className="create-btn-secondary" onClick={handleCreate}>
            {t('admin.images.createBtn')}
          </button>
        </div>
      ) : (
        <>
          <div className="items-grid" ref={gridRef} style={{ position: 'relative', minHeight: (listPagesApi.loading || getPageImagesApi.loading) ? '400px' : 'auto' }}>
            {(listPagesApi.loading || getPageImagesApi.loading) && allImages.length > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px'
              }}>
                <LoadingSpinner message={t('common.loading', 'Loading...')} />
              </div>
            )}
            {displayedImages.map(imageItem => (
              <div key={imageItem.id} className="item-card" onClick={() => handleEdit(imageItem)}>
                <div className="item-card-body">
                  <div className="item-id">#{imageItem.id}</div>
                  <h3 className="item-title">{getLocalizedField(imageItem, 'title') || imageItem.image_filename || 'Untitled Image'}</h3>
                  <div className="item-meta">
                    <span className="item-date">
                      {imageItem.page_id ? getPageName(imageItem.page_id) : '—'}
                    </span>
                    {imageItem.private && <span className="item-badge">Members Only</span>}
                  </div>
                </div>
                <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="item-copy-btn" onClick={() => handleCopy(imageItem)} title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    Copy
                  </button>
                  <button className="item-delete-btn" onClick={() => handleDelete(imageItem.id)} title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImagesTab;
